/**
 * @fileoverview useKeyboardNavigation Hook - Keyboard controls for floor plan canvas
 * @author MCHD Room Scheduler Team
 * @created 09-10-2025 04:30:44
 * 
 * Custom hook for keyboard navigation on floor plan canvas.
 * Per Floor Plan Spec Section 3 & 11:
 * 
 * Supported Keys:
 * - Arrow keys: Move selection between rooms (reading order)
 * - Enter: Open Assign modal for selected room
 * - Esc: Clear selection or close sidebar
 * - Plus/Equals: Zoom in
 * - Minus: Zoom out
 * - Zero: Reset zoom to 100%
 * 
 * Reading order logic:
 * - Rooms arranged in grid (4 per row based on position formula)
 * - Right arrow: Next room (index + 1)
 * - Left arrow: Previous room (index - 1)
 * - Down arrow: Next row (index + 4)
 * - Up arrow: Previous row (index - 4)
 */

import { useEffect, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface Room {
  id: string;
  number: string;
  position?: {
    x: number;
    y: number;
  };
}

export interface UseKeyboardNavigationOptions {
  /** List of rooms in reading order */
  rooms: Room[];
  
  /** Currently selected room */
  selectedRoom: Room | null;
  
  /** Callback when room selection changes */
  onSelectRoom: (room: Room | null) => void;
  
  /** Callback to open assign modal */
  onOpenAssignModal: (room: Room) => void;
  
  /** Callback to zoom in */
  onZoomIn: () => void;
  
  /** Callback to zoom out */
  onZoomOut: () => void;
  
  /** Callback to reset zoom */
  onResetZoom: () => void;
  
  /** Enable/disable keyboard navigation */
  enabled?: boolean;
  
  /** Rooms per row in grid layout (default: 4) */
  roomsPerRow?: number;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * useKeyboardNavigation - Keyboard controls for floor plan
 * 
 * Manages keyboard shortcuts for efficient floor plan navigation.
 * Prevents default browser behavior for navigation keys.
 * 
 * Grid layout calculation:
 * - Position formula: x = (index % 4) * 200 + 50
 * - This means 4 rooms per row
 * - Arrow keys navigate based on this grid
 * 
 * @param options - Configuration object
 * @returns void (side effects only via callbacks)
 * 
 * @example
 * ```tsx
 * useKeyboardNavigation({
 *   rooms: floorPlanData.rooms,
 *   selectedRoom: currentRoom,
 *   onSelectRoom: (room) => setSelectedRoom(room),
 *   onOpenAssignModal: (room) => openModal(room),
 *   onZoomIn: () => zoom(1.2),
 *   onZoomOut: () => zoom(0.8),
 *   onResetZoom: () => setZoom(1.0),
 *   enabled: true
 * });
 * ```
 */
export function useKeyboardNavigation({
  rooms,
  selectedRoom,
  onSelectRoom,
  onOpenAssignModal,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  enabled = true,
  roomsPerRow = 4
}: UseKeyboardNavigationOptions): void {
  
  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;
    
    // Find current room index
    const currentIndex = selectedRoom 
      ? rooms.findIndex(r => r.id === selectedRoom.id)
      : -1;
    
    let nextRoom: Room | null = null;
    let shouldPreventDefault = false;
    
    switch (e.key) {
      // Arrow navigation
      case 'ArrowRight':
        if (currentIndex >= 0 && currentIndex < rooms.length - 1) {
          nextRoom = rooms[currentIndex + 1];
          shouldPreventDefault = true;
        }
        break;
        
      case 'ArrowLeft':
        if (currentIndex > 0) {
          nextRoom = rooms[currentIndex - 1];
          shouldPreventDefault = true;
        }
        break;
        
      case 'ArrowDown':
        // Move down one row (+ roomsPerRow positions)
        if (currentIndex >= 0 && currentIndex + roomsPerRow < rooms.length) {
          nextRoom = rooms[currentIndex + roomsPerRow];
          shouldPreventDefault = true;
        }
        break;
        
      case 'ArrowUp':
        // Move up one row (- roomsPerRow positions)
        if (currentIndex >= roomsPerRow) {
          nextRoom = rooms[currentIndex - roomsPerRow];
          shouldPreventDefault = true;
        }
        break;
      
      // Enter: Open assign modal
      case 'Enter':
        if (selectedRoom) {
          onOpenAssignModal(selectedRoom);
          shouldPreventDefault = true;
        }
        break;
      
      // Escape: Clear selection
      case 'Escape':
        if (selectedRoom) {
          onSelectRoom(null);
          shouldPreventDefault = true;
        }
        break;
      
      // Zoom controls
      case '+':
      case '=': // Plus key without shift
        onZoomIn();
        shouldPreventDefault = true;
        break;
        
      case '-':
      case '_': // Minus key
        onZoomOut();
        shouldPreventDefault = true;
        break;
        
      case '0':
        onResetZoom();
        shouldPreventDefault = true;
        break;
    }
    
    // Update selection if new room found
    if (nextRoom) {
      onSelectRoom(nextRoom);
    }
    
    // Prevent default browser behavior for navigation keys
    if (shouldPreventDefault) {
      e.preventDefault();
    }
    
  }, [
    enabled,
    rooms,
    selectedRoom,
    onSelectRoom,
    onOpenAssignModal,
    onZoomIn,
    onZoomOut,
    onResetZoom,
    roomsPerRow
  ]);
  
  /**
   * Attach/detach keyboard listener
   */
  useEffect(() => {
    if (!enabled) return;
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}

export default useKeyboardNavigation;

